import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Box, Button, Chip, LinearProgress,
  IconButton, Menu, MenuItem, Tooltip, Avatar, AvatarGroup, Divider
} from '@mui/material';
import {
  LocationOn, AttachMoney, Person, MoreVert, Edit, Payment,
  CheckCircle, Schedule, Warning, TrendingUp, Flag
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { brand, status as statusColors, border } from '../../theme/colors';

/**
 * Improved Project Card Component
 * Modern, clean design with better information hierarchy
 */
const ImprovedProjectCard = ({ project, onEdit, onPayment }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  // Calculate milestone progress
  const getMilestoneInfo = () => {
    const total = project.milestoneCount || 0;
    const completed = Math.floor((total * (project.progress || 0)) / 100);
    return { completed, total };
  };

  // Get status color and label
  const getStatusInfo = () => {
    const projectStatus = project.status || 'planning';
    const statusMap = {
      'planning': { color: statusColors.neutral, label: 'Planning', icon: <Schedule /> },
      'open_for_bids': { color: brand.primary, label: 'Open for Bids', icon: <Flag /> },
      'in_progress': { color: statusColors.success, label: 'In Progress', icon: <TrendingUp /> },
      'active': { color: statusColors.success, label: 'Active', icon: <CheckCircle /> },
      'completed': { color: statusColors.success, label: 'Completed', icon: <CheckCircle /> },
      'on_hold': { color: statusColors.warning, label: 'On Hold', icon: <Warning /> },
      'cancelled': { color: statusColors.error, label: 'Cancelled', icon: <Warning /> }
    };
    return statusMap[projectStatus] || statusMap['planning'];
  };

  // Get progress color based on status
  const getProgressColor = () => {
    const progress = parseFloat(project.progress || 0);
    if (progress >= 75) return statusColors.success; // Green
    if (progress >= 50) return statusColors.warning; // Yellow
    if (progress >= 25) return statusColors.info; // Blue
    return statusColors.neutral; // Gray
  };

  // Format budget. An open/unset budget (0) means the owner is building as they
  // go — show that instead of a misleading "$0".
  const formatBudget = (amount) => {
    if (!amount || Number(amount) <= 0) return 'Open / to be discussed';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format location (shorten if too long)
  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    const parts = location.split(',').map(p => p.trim());
    // Return last 2 parts (city, state/country)
    return parts.slice(-2).join(', ');
  };

  // Get contractor display
  const getContractorDisplay = () => {
    if (project.contractor?.name) {
      return project.contractor.name;
    }
    return 'Awaiting Assignment';
  };

  const statusInfo = getStatusInfo();
  const milestoneInfo = getMilestoneInfo();
  const progressColor = getProgressColor();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit(project);
  };

  const handlePayment = () => {
    handleMenuClose();
    onPayment(project);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: `1px solid ${border.default}`,
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          borderColor: brand.primary
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header with Title and Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '20px',
                color: '#1a1a1a',
                mb: 0.5,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {project.title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.label}
              size="small"
              sx={{
                backgroundColor: `${statusInfo.color}15`,
                color: statusInfo.color,
                fontWeight: 600,
                border: `1px solid ${statusInfo.color}40`,
                '& .MuiChip-icon': {
                  color: statusInfo.color
                }
              }}
            />
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <LocationOn sx={{ fontSize: 18, color: '#666' }} />
          <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
            {formatLocation(project.location)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Budget and Contractor Info */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AttachMoney sx={{ fontSize: 18, color: '#333' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
              Budget:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: brand.primary }}>
              {formatBudget(project.budget)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ fontSize: 18, color: '#333' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
              Contractor:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: project.contractor?.name ? '#333' : '#999',
                fontStyle: project.contractor?.name ? 'normal' : 'italic'
              }}
            >
              {getContractorDisplay()}
            </Typography>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
              Progress
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: progressColor, fontSize: '14px' }}
            >
              {Math.round(project.progress || 0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={project.progress || 0}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: `${progressColor}20`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: progressColor,
                borderRadius: 4
              }
            }}
          />
          {milestoneInfo.total > 0 && (
            <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
              {milestoneInfo.completed} of {milestoneInfo.total} milestones
              {project.nextMilestone && ` • Next: ${project.nextMilestone}`}
            </Typography>
          )}
        </Box>

        {/* AI Insight (if available) */}
        {project.aiInsight && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              background: 'linear-gradient(135deg, rgba(13,148,136,0.08) 0%, rgba(19,78,74,0.08) 100%)',
              border: '1px solid rgba(13,148,136,0.19)',
              mb: 2
            }}
          >
            <Typography variant="caption" sx={{ color: brand.primary, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp sx={{ fontSize: 14 }} />
              AI Insight
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
              {project.aiInsight}
            </Typography>
          </Box>
        )}

        {/* Primary and common secondary actions */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 'auto' }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/projects/${project.id}`)}
            sx={{
              gridColumn: '1 / -1',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
              py: 1.2,
              background: brand.gradient,
              '&:hover': {
                background: brand.gradientHover,
                transform: 'scale(1.02)'
              }
            }}
          >
            View Details
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={() => onEdit(project)}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              borderColor: brand.primary,
              color: brand.primary,
              '&:hover': { borderColor: brand.primaryDark, bgcolor: 'rgba(13,148,136,0.06)' }
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Payment />}
            onClick={() => onPayment(project)}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              borderColor: brand.primary,
              color: brand.primary,
              '&:hover': { borderColor: brand.primaryDark, bgcolor: 'rgba(13,148,136,0.06)' }
            }}
          >
            Payment
          </Button>
        </Box>
      </CardContent>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Project
        </MenuItem>
        <MenuItem onClick={handlePayment}>
          <Payment fontSize="small" sx={{ mr: 1 }} />
          Make Payment
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default ImprovedProjectCard;
